import { createContext, useContext, useState, useEffect } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    // Load from local storage on init
    const savedCart = localStorage.getItem("nexsales_cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Save to local storage on change
    localStorage.setItem("nexsales_cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, quantity) => {
    // Check existence in current state to decide message
    // (We do this outside setCart to avoid double-toasts in React Strict Mode)
    const existingItem = cart.find((item) => item.id === product.id);
    
    if (existingItem) {
      toast.info("Quantità aggiornata nel carrello");
    } else {
      toast.success("Prodotto aggiunto al carrello");
    }

    setCart((prevCart) => {
      const existing = prevCart.find((item) => item.id === product.id);
      if (existing) {
        return prevCart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prevCart, { ...product, quantity }];
    });
  };

  const removeFromCart = (productId) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== productId));
    toast.info("Prodotto rimosso");
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity < 1) return;
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  
  // Estimate total (base commercial price * qty) - Taxes/Discounts calculated by backend
  const estimatedTotal = cart.reduce(
    (sum, item) => sum + item.commercial_price * item.quantity,
    0
  );

  const value = {
    cart,
    isOpen,
    setIsOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    totalItems,
    estimatedTotal,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
